/**
 * Software savings from Airtable discount rows matched to a company (tech stack).
 * Mirrors SoftwareSavingsPage: dedupe by AssociatedTechnology (first row wins),
 * then sum AnnualMargin, else MaxSavings per row.
 *
 * @param {Array<{ fields?: Record<string, unknown> }>} matchedDiscounts
 * @returns {number}
 */
export function sumSoftwareSavingsFromMatchedDiscounts(matchedDiscounts) {
  if (!Array.isArray(matchedDiscounts) || matchedDiscounts.length === 0) return 0;
  const temp = [];
  for (let i = 0; i < matchedDiscounts.length; i++) {
    const row = matchedDiscounts[i];
    const tech = row?.fields?.AssociatedTechnology;
    if (temp.some((a) => a.fields.AssociatedTechnology === tech)) continue;
    temp.push(row);
  }
  return temp.reduce((partialSum, a) => {
    const m = a?.fields?.AnnualMargin;
    if (m != null && m !== "") {
      const n = typeof m === "number" ? m : parseFloat(String(m).replace(/[$,]/g, ""));
      if (!Number.isNaN(n)) return partialSum + n;
    }
    const ms = a?.fields?.MaxSavings;
    if (ms != null && ms !== "") {
      const n = typeof ms === "number" ? ms : parseFloat(String(ms).replace(/[$,]/g, ""));
      if (!Number.isNaN(n)) return partialSum + n;
    }
    return partialSum;
  }, 0);
}

/**
 * When matched savings are 0 or unavailable, show a plausible annual figure from spend fields.
 * @param {Record<string, unknown>|undefined} fields company.fields
 * @returns {number}
 */
export function defaultSoftwareSavingsAnnualEstimate(fields) {
  if (!fields || typeof fields !== "object") return 100_000;
  const monthly =
    typeof fields.MonthlySoftwareSpend === "number"
      ? fields.MonthlySoftwareSpend
      : parseFloat(String(fields.MonthlySoftwareSpend ?? "").replace(/[$,]/g, ""));
  if (Number.isFinite(monthly) && monthly > 0) {
    return Math.round(monthly * 12 * 0.12);
  }
  const sw =
    typeof fields.SoftwareExpenses === "number"
      ? fields.SoftwareExpenses
      : parseFloat(String(fields.SoftwareExpenses ?? "").replace(/[$,]/g, ""));
  if (Number.isFinite(sw) && sw > 0) {
    return Math.round(sw * 0.12);
  }
  return 100_000;
}
